import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { ReactNode } from 'react';

export type PageTitleOverride = { title?: ReactNode; section?: ReactNode };

type PageTitleContextValue = {
  override: PageTitleOverride | null;
  setOverride: (value: PageTitleOverride | null) => void;
};

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

/** Holds the current page's title/section override for the topbar breadcrumb. */
export const PageTitleProvider = ({ children }: { children: ReactNode }) => {
  const [override, setOverride] = useState<PageTitleOverride | null>(null);
  const value = useMemo(() => ({ override, setOverride }), [override]);
  return <PageTitleContext value={value}>{children}</PageTitleContext>;
};

/**
 * Sets the topbar breadcrumb title/section for the lifetime of the calling
 * component (cleared on unmount). Use for titles that need fetched data (e.g.
 * the Markets page setting the market's display name). Routes with a static or
 * param-derived title should use the route `handle` instead.
 */
export const useSetPageTitle = (meta: PageTitleOverride) => {
  const ctx = useContext(PageTitleContext);
  const { title, section } = meta;
  // Depend on the stable `setOverride` function (from useState, never changes
  // identity) rather than `ctx` itself. `ctx` is a new object every time the
  // provider's `override` state changes (see PageTitleProvider's useMemo), so
  // depending on `ctx` here would re-run this effect every time it calls
  // setOverride, causing an infinite render loop.
  const setOverride = ctx?.setOverride;
  useEffect(() => {
    setOverride?.({ title, section });
    return () => setOverride?.(null);
  }, [setOverride, title, section]);
};

export { PageTitleContext };
